import React, { useState } from "react";
import { Box, Text, Flex, Image, Button, Divider } from "@chakra-ui/react";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { FieldArray, Form, Formik } from "formik";

import Topbar from "components/topbar";
import MainLayout from "components/mainLayout";
import TextInput from "./components/TextInput";
import FormikCheckbox from "components/formikCheckbox";
import { useShipmentForm } from "context/DHLShipmentFormContext";
import FormikSelect from "components/formikSelect";
import { useUserContext } from "context/UserContext";
import SearchAbleSelectInput from "./components/SearchAbleSelectInput";
import { countryCodes } from "./data/countryCodes";
import useGetServicePointLocation from "api/queries/useGetServicePointLocation";

const AddressDetails = () => {
  const navigate = useNavigate();
  const { user } = useUserContext();
  const {
    formData,
    updateFormData,
    selectFromCountry,
    setSelectFromCountry,
    selectFromCountryCode,
    setSelectFromCountryCode,
    selectToCountry,
    setSelectToCountry,
    selectToCountryCode,
    setSelectToCountryCode,
  } = useShipmentForm();
  // const [selectFromPostalCode, setSelectFromPostalCode] = useState({});
  // const [selectFromCity, setSelectFromCity] = useState({});
  // const [selectToPostalCode, setSelectToPostalCode] = useState({});
  // const [selectToCity, setSelectToCity] = useState({});

  const phoneSchema = Yup.object().shape({
    type: Yup.string().required("Phone type is required*"),
    code: Yup.string().required("Phone code is required*"),
    number: Yup.string().required("Phone number is required*"),
  });

  // const registrationSchema = Yup.object().shape({
  //   number: Yup.string().required("Registration number is required*"),
  //   countryCode: Yup.string().required("Country code is required*"),
  //   typeCode: Yup.string().required("Type code is required*"),
  // });

  const addressSchema = Yup.object().shape({
    postalCode: Yup.string().required("Postal code is required*"),
    city: Yup.string().required("City is required*"),
    residentialAddress: Yup.boolean(),
    country: Yup.string().required("Country is required*"),
    address: Yup.object().shape({
      one: Yup.string().required("Address line 1 is required*"),
      two: Yup.string().required("Address line 2 is required*"),
      three: Yup.string(),
    }),
  });

  const contactSchema = Yup.object().shape({
    name: Yup.string().required("Name is required*"),
    companyName: Yup.string().required("Company name is required*"),
    businessContact: Yup.boolean(),
    email: Yup.string().email("Invalid email").required("Email is required*"),
    smsEnabled: Yup.boolean(),
    phone: Yup.array()
      .of(phoneSchema)
      .min(1, "At least one phone is required*"),
  });

  const validationSchema = Yup.object().shape({
    from: Yup.object().shape({
      address: addressSchema,
      contact: contactSchema,
      //   registrationNumbers: Yup.array()
      //     .of(registrationSchema)
      //     .min(1, "At least one registration number is required*"),
    }),
    to: Yup.object().shape({
      address: addressSchema,
      contact: contactSchema,
      // registrationNumbers: Yup.array()
      //   .of(registrationSchema)
      //   .min(1, "At least one registration number is required*"),
    }),
  });

  const initialValues = {
    from: {
      address: {
        postalCode: formData?.details?.from?.address?.postalCode,
        city: formData?.details?.from?.address?.city,
        residentialAddress:
          formData?.details?.from?.address?.residentialAddress,
        country: formData?.details?.from?.address?.country,
        address: {
          one: formData?.details?.from?.address?.address?.one,
          two: formData?.details?.from?.address?.address?.two,
          three: formData?.details?.from?.address?.address?.three,
        },
      },
      contact: {
        name: formData?.details?.from?.contact?.name,
        companyName: formData?.details?.from?.contact?.companyName,
        businessContact: formData?.details?.from?.contact?.businessContact,
        email: formData?.details?.from?.contact?.email,
        smsEnabled: formData?.details?.from?.contact?.smsEnabled,
        phone: formData?.details?.from?.contact?.phone,
      },
      registrationNumbers: [],
    },
    to: {
      address: {
        postalCode: formData?.details?.to?.address?.postalCode,
        city: formData?.details?.to?.address?.city,
        residentialAddress: formData?.details?.to?.address?.residentialAddress,
        country: formData?.details?.to?.address?.country,
        address: {
          one: formData?.details?.to?.address?.address?.one,
          two: formData?.details?.to?.address?.address?.two,
          three: formData?.details?.to?.address?.address?.three,
        },
      },
      contact: {
        name: formData?.details?.to?.contact?.name,
        companyName: formData?.details?.to?.contact?.companyName,
        businessContact: formData?.details?.to?.contact?.businessContact,
        email: formData?.details?.to?.contact?.email,
        smsEnabled: formData?.details?.to?.contact?.smsEnabled,
        phone: formData?.details?.to?.contact?.phone,
      },
      registrationNumbers: [],
    },
  };

  // const { data, isLoading, error, refetch, isFetching } =
  //   useGetServicePointLocation({
  //     postalCode: selectFromCountry,
  //     countryCode: "GB",
  //   });

  return (
    <MainLayout>
      <Box minH={"100vh"} h={"100%"}>
        <Topbar title={"SHIP THROUGH DHL"} />
        <Flex bg={"#f8fbff"} padding={4} gap={2}>
          <Flex
            flex={1}
            direction={"column"}
            justifyContent={"space-between"}
            bg={"#FFFFFF"}
            padding={6}
            maxHeight={"100%"}
            borderRadius={"16px"}
            gap={6}
          >
            {/* <Box> */}
            <Text
              fontFamily={"PlusJakartaSansSemiBold"}
              fontSize={"24px"}
              lineHeight={"32px"}
              textTransform={"capitalize"}
              color={"#000000"}
            >
              Create Shipment
            </Text>
            <Divider bg={"#8A8F9333"} h={"1px"} />
            <Box>
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  updateFormData({
                    details: values,
                    requested_by: user?.info?._id,
                  });
                  navigate("/send-a-bag/dhl/shipment-type");
                }}
              >
                {(formik) => (
                  <Form>
                    <Flex flexDirection={"column"} gap={6}>
                      {/* From */}
                      <Box>
                        <Text
                          fontFamily={"PlusJakartaSansSemiBold"}
                          fontSize={"16px"}
                          lineHeight={"20px"}
                          textTransform={"capitalize"}
                          color={"#000000"}
                        >
                          From
                        </Text>
                        <Flex
                          gap={4}
                          marginTop={2}
                          borderWidth={"1px"}
                          borderColor={"#D9D9D9"}
                          borderStyle={"solid"}
                          padding={4}
                          borderRadius={"6px"}
                          flexDirection={"column"}
                        >
                          <Flex gap={4}>
                            <TextInput
                              formik={formik}
                              name="from.contact.name"
                              label="Name"
                              placeholder="Enter Name"
                            />
                             <TextInput
                              formik={formik}
                              name="from.contact.companyName"
                              label={"Company Name"}
                              placeholder="Enter Company Name"
                              disabled
                            />
                          </Flex>
                          <Flex gap={1} alignItems={"center"}>
                            <FormikCheckbox
                              id="from.contact.businessContact"
                              name="from.contact.businessContact"
                              isChecked={
                                formik.values.from.contact.businessContact
                              }
                              onChange={formik.handleChange}
                            />
                            <Text
                              fontFamily={"PlusJakartaSansMedium"}
                              fontSize={"12px"}
                              textTransform={"capitalize"}
                              color={"#000000"}
                              mb={1}
                            >
                              Business Contact
                            </Text>
                          </Flex>
                          <Flex gap={4}>
                            <SearchAbleSelectInput
                              name="from.address.country"
                              label={"Country"}
                              placeholder={"Select Country"}
                              onChangeHandler={(value) => {
                                setSelectFromCountry({
                                  label: value?.label,
                                  value: value?.value?.code,
                                });
                                setSelectFromCountryCode({
                                  label: value?.value?.phoneCode,
                                  value: value?.value?.phoneCode,
                                });
                                formik.setFieldValue(
                                  "from.address.country",
                                  value?.value?.code
                                );
                                formik.setFieldValue(
                                  `from.contact.phone[${0}].code`,
                                  value?.value?.phoneCode
                                );
                              }}
                              selectedValue={selectFromCountry}
                              disabled
                            >
                              {countryCodes.map((opt) => (
                                <option key={opt.code} value={opt}>
                                  {opt.name}
                                </option>
                              ))}
                            </SearchAbleSelectInput>
                            <TextInput
                              formik={formik}
                              name="from.contact.email"
                              label={"Email Address"}
                              placeholder={"Enter Email Address"}
                            />
                          </Flex>
                          <Flex gap={4}>
                            <TextInput
                              formik={formik}
                              name="from.address.address.one"
                              label={"Address 1"}
                              placeholder={"Enter Address 1"}
                            />
                          </Flex>
                          <Flex gap={4}>
                            <TextInput
                              formik={formik}
                              name="from.address.address.two"
                              label={"Address 2"}
                              placeholder={"Enter Address 2"}
                            />
                            {/* <TextInput
                              formik={formik}
                              name="from.address.address.three"
                              label={"Address 3"}
                              placeholder={"Enter Address 3"}
                            /> */}
                          </Flex>
                          <Flex gap={4}>
                            <TextInput
                              formik={formik}
                              name="from.address.postalCode"
                              label={"Postal Code"}
                              placeholder={"Enter Postal Code"}
                            />
                            <TextInput
                              formik={formik}
                              name="from.address.city"
                              label={"City"}
                              placeholder={"Enter City"}
                            />
                          </Flex>
                          <Flex gap={1} alignItems={"center"}>
                            <FormikCheckbox
                              id="from.address.residentialAddress"
                              name="from.address.residentialAddress"
                              isChecked={
                                formik.values.from.address.residentialAddress
                              }
                              onChange={formik.handleChange}
                            />
                            <Text
                              fontFamily={"PlusJakartaSansMedium"}
                              fontSize={"12px"}
                              textTransform={"capitalize"}
                              color={"#000000"}
                              mb={1}
                            >
                              Residential Address
                            </Text>
                          </Flex>
                          <FieldArray name="from.contact.phone">
                            {({ push, remove }) => (
                              <>
                                {formik.values.from.contact.phone.map(
                                  (_, index) => (
                                    <Flex key={index} gap={4} mb={2}>
                                      {/* <TextInput
                                        formik={formik}
                                        name={`from.contact.phone[${index}].type`}
                                        label={"Phone Type"}
                                        placeholder={"Enter Phone Type"}
                                      /> */}
                                      <FormikSelect
                                        formik={formik}
                                        name={`from.contact.phone[${index}].type`}
                                        label={"Phone Type"}
                                      >
                                        <option value="mobile">Mobile</option>
                                      </FormikSelect>
                                      {/* <TextInput
                                        formik={formik}
                                        name={`from.contact.phone[${index}].code`}
                                        label={"Code"}
                                        placeholder={"+44"}
                                      /> */}
                                      <SearchAbleSelectInput
                                        name={`from.contact.phone[${index}].code`}
                                        label={"Code"}
                                        placeholder={"Select Code"}
                                        onChangeHandler={(value) => {
                                          setSelectFromCountryCode(value);
                                          formik.setFieldValue(
                                            `from.contact.phone[${index}].code`,
                                            value?.value
                                          );
                                        }}
                                        selectedValue={selectFromCountryCode}
                                        disabled
                                      >
                                        {countryCodes.map((opt) => (
                                          <option
                                            key={opt.phoneCode}
                                            value={opt.phoneCode}
                                          >
                                            {opt.phoneCode}
                                          </option>
                                        ))}
                                      </SearchAbleSelectInput>
                                      <TextInput
                                        formik={formik}
                                        name={`from.contact.phone[${index}].number`}
                                        label={"Phone"}
                                        placeholder={"0751 5360049"}
                                      />
                                      {index > 0 && (
                                        <Button
                                          onClick={() => remove(index)}
                                          w={"100px"}
                                          h={"30px"}
                                          py={5}
                                          bg={"#FF5252"}
                                          borderRadius={"4px"}
                                          _hover={"none"}
                                          alignSelf={"flex-end"}
                                          alignItems={"center"}
                                          justifyContent={"center"}
                                        >
                                          <Image
                                            src={
                                              require("assets/svgs/cross.svg")
                                                .default
                                            }
                                            width={"22px"}
                                            height={"22px"}
                                            stroke={"#ffffff"}
                                            pos={"relative"}
                                            _hover={{
                                              cursor: "pointer",
                                            }}
                                          />
                                        </Button>
                                      )}
                                    </Flex>
                                  )
                                )}

                                {/* <Flex gap={1} alignItems={"center"} mb={4}>
                                  <FormikCheckbox
                                    id="smsEnabled"
                                    name="from.contact.smsEnabled"
                                    isChecked={
                                      formik.values.from.contact.smsEnabled
                                    }
                                    onChange={formik.handleChange}
                                  />
                                  <Text
                                    fontFamily={"PlusJakartaSansMedium"}
                                    fontSize={"12px"}
                                    textTransform={"capitalize"}
                                    color={"#000000"}
                                  >
                                    SMS Enabled
                                  </Text>
                                </Flex> */}

                                <Flex gap={4}>
                                  <Button
                                    onClick={() =>
                                      push({
                                        type: "",
                                        code: "",
                                        number: "",
                                      })
                                    }
                                    w={"120px"}
                                    h={"40px"}
                                    py={6}
                                    bg={"#D9D9D9"}
                                    borderRadius={"4px"}
                                    color={"#444444"}
                                    alignSelf={"flex-end"}
                                    alignItems={"center"}
                                    justifyContent={"center"}
                                    fontSize={"12px"}
                                    fontFamily={"PlusJakartaSansRegular"}
                                  >
                                    <Image
                                      src={
                                        require("assets/svgs/plus_dark.svg")
                                          .default
                                      }
                                      width={"16px"}
                                      height={"16px"}
                                      pos={"relative"}
                                      mr={2}
                                      _hover={{
                                        cursor: "pointer",
                                      }}
                                    />
                                    Add another
                                  </Button>
                                </Flex>
                              </>
                            )}
                          </FieldArray>
                        </Flex>
                      </Box>
                      {/* Switch */}
                      {/* <Box>
                        <Flex justifyContent={"center"}>
                          <Button
                            onClick={() => {
                              const fromValues = formik.values.from;
                              const toValues = formik.values.to;

                              setSelectFromCountry(selectToCountry);
                              setSelectFromCountryCode(selectToCountryCode);
                              setSelectToCountry(selectFromCountry);
                              setSelectToCountryCode(selectFromCountryCode);

                              // Swap the values
                              formik.setValues({
                                ...formik.values,
                                from: toValues,
                                to: fromValues,
                              });
                            }}
                            w={"87px"}
                            h={"40px"}
                            py={6}
                            bg={"#456EFF"}
                            borderRadius={"4px"}
                            color={"#FFFFFF"}
                            alignSelf={"flex-end"}
                            alignItems={"center"}
                            justifyContent={"center"}
                            fontSize={"12px"}
                            fontFamily={"PlusJakartaSansRegular"}
                          >
                            <Image
                              src={
                                require("assets/svgs/switch-vertical.svg")
                                  .default
                              }
                              width={"16px"}
                              height={"16px"}
                              pos={"relative"}
                              mr={1}
                              _hover={{
                                cursor: "pointer",
                              }}
                            />
                            Switch
                          </Button>
                        </Flex>
                      </Box> */}
                      {/* TO */}
                      <Box>
                        <Text
                          fontFamily={"PlusJakartaSansSemiBold"}
                          fontSize={"16px"}
                          lineHeight={"20px"}
                          textTransform={"capitalize"}
                          color={"#000000"}
                        >
                          To
                        </Text>
                        <Flex
                          gap={4}
                          marginTop={2}
                          borderWidth={"1px"}
                          borderColor={"#D9D9D9"}
                          borderStyle={"solid"}
                          padding={4}
                          borderRadius={"6px"}
                          flexDirection={"column"}
                        >
                          <Flex gap={4}>
                            <TextInput
                              formik={formik}
                              name="to.contact.name"
                              label="Name"
                              placeholder="Enter Name"
                              disabled
                            />
                            <TextInput
                              formik={formik}
                              name="to.contact.companyName"
                              label={"Company Name"}
                              placeholder="Enter Company Name"
                              disabled
                            />
                          </Flex>
                          <Flex gap={1} alignItems={"center"}>
                            <FormikCheckbox
                              id="to.contact.businessContact"
                              name="to.contact.businessContact"
                              isChecked={
                                formik.values.to.contact.businessContact
                              }
                              onChange={formik.handleChange}
                            />
                            <Text
                              fontFamily={"PlusJakartaSansMedium"}
                              fontSize={"12px"}
                              textTransform={"capitalize"}
                              color={"#000000"}
                              mb={1}
                            >
                              Business Contact
                            </Text>
                          </Flex>
                          <Flex gap={4}>
                            <SearchAbleSelectInput
                              name="to.address.country"
                              label={"Country"}
                              placeholder={"Select Country"}
                              onChangeHandler={(value) => {
                                setSelectToCountry({
                                  label: value?.label,
                                  value: value?.value?.code,
                                });
                                setSelectToCountryCode({
                                  label: value?.value?.phoneCode,
                                  value: value?.value?.phoneCode,
                                });
                                formik.setFieldValue(
                                  "to.address.country",
                                  value?.value?.code
                                );
                                formik.setFieldValue(
                                  `to.contact.phone[${0}].code`,
                                  value?.value?.phoneCode
                                );
                              }}
                              selectedValue={selectToCountry}
                              disabled
                            >
                              {countryCodes.map((opt) => (
                                <option key={opt.code} value={opt}>
                                  {opt.name}
                                </option>
                              ))}
                            </SearchAbleSelectInput>
                            <TextInput
                              formik={formik}
                              name="to.contact.email"
                              label={"Email Address"}
                              placeholder={"Enter Email Address"}
                              disabled
                            />
                          </Flex>
                          <Flex gap={4}>
                            <TextInput
                              formik={formik}
                              name="to.address.address.one"
                              label={"Address 1"}
                              placeholder={"Enter Address 1"}
                              disabled
                            />
                          </Flex>
                          <Flex gap={4}>
                            <TextInput
                              formik={formik}
                              name="to.address.address.two"
                              label={"Address 2"}
                              placeholder={"Enter Address 2"}
                              disabled
                            />
                            {/* <TextInput
                              formik={formik}
                              name="to.address.address.three"
                              label={"Address 3"}
                              placeholder={"Enter Address 3"}
                            /> */}
                          </Flex>
                          <Flex gap={4}>
                            <TextInput
                              formik={formik}
                              name="to.address.postalCode"
                              label={"Postal Code"}
                              placeholder={"Enter Postal Code"}
                              disabled
                            />
                            <TextInput
                              formik={formik}
                              name="to.address.city"
                              label={"City"}
                              placeholder={"Enter City"}
                              disabled
                            />
                          </Flex>
                          {/* <Flex gap={1} alignItems={"center"}>
                            <FormikCheckbox
                              id="to.address.residentialAddress"
                              name="to.address.residentialAddress"
                              isChecked={
                                formik.values.to.address.residentialAddress
                              }
                              onChange={formik.handleChange}
                            />
                            <Text
                              fontFamily={"PlusJakartaSansMedium"}
                              fontSize={"12px"}
                              textTransform={"capitalize"}
                              color={"#000000"}
                              mb={1}
                            >
                              Residential Address
                            </Text>
                          </Flex> */}
                          <FieldArray name="to.contact.phone">
                            {({ push, remove }) => (
                              <>
                                {formik.values.to.contact.phone.map(
                                  (_, index) => (
                                    <Flex key={index} gap={4} mb={2}>
                                      <FormikSelect
                                        formik={formik}
                                        name={`to.contact.phone[${index}].type`}
                                        label={"Phone Type"}
                                        disabled
                                      >
                                        <option value="mobile">Mobile</option>
                                      </FormikSelect>
                                      {/* <TextInput
                                        formik={formik}
                                        name={`to.contact.phone[${index}].type`}
                                        label={"Phone Type"}
                                        placeholder={"Enter Phone Type"}
                                      /> */}
                                      {/* <TextInput
                                        formik={formik}
                                        name={`to.contact.phone[${index}].code`}
                                        label={"Code"}
                                        placeholder={"+44"}
                                      /> */}
                                      <SearchAbleSelectInput
                                        name={`to.contact.phone[${index}].code`}
                                        label={"Code"}
                                        placeholder={"Select Code"}
                                        onChangeHandler={(value) => {
                                          setSelectToCountryCode(value);
                                          formik.setFieldValue(
                                            `to.contact.phone[${index}].code`,
                                            value?.value
                                          );
                                        }}
                                        selectedValue={selectToCountryCode}
                                        disabled
                                      >
                                        {countryCodes.map((opt) => (
                                          <option
                                            key={opt.phoneCode}
                                            value={opt.phoneCode}
                                          >
                                            {opt.phoneCode}
                                          </option>
                                        ))}
                                      </SearchAbleSelectInput>
                                      <TextInput
                                        formik={formik}
                                        name={`to.contact.phone[${index}].number`}
                                        label={"Phone"}
                                        placeholder={"0751 5360049"}
                                        disabled
                                      />
                                      {index > 0 && (
                                        <Button
                                          onClick={() => remove(index)}
                                          w={"100px"}
                                          h={"30px"}
                                          py={5}
                                          bg={"#FF5252"}
                                          borderRadius={"4px"}
                                          _hover={"none"}
                                          alignSelf={"flex-end"}
                                          alignItems={"center"}
                                          justifyContent={"center"}
                                        >
                                          <Image
                                            src={
                                              require("assets/svgs/cross.svg")
                                                .default
                                            }
                                            width={"22px"}
                                            height={"22px"}
                                            stroke={"#ffffff"}
                                            pos={"relative"}
                                            _hover={{
                                              cursor: "pointer",
                                            }}
                                          />
                                        </Button>
                                      )}
                                    </Flex>
                                  )
                                )}

                                {/* <Flex gap={1} alignItems={"center"} mb={4}>
                                  <FormikCheckbox
                                    id="smsEnabled"
                                    name="to.contact.smsEnabled"
                                    isChecked={
                                      formik.values.to.contact.smsEnabled
                                    }
                                    onChange={formik.handleChange}
                                  />
                                  <Text
                                    fontFamily={"PlusJakartaSansMedium"}
                                    fontSize={"12px"}
                                    textTransform={"capitalize"}
                                    color={"#000000"}
                                  >
                                    SMS Enabled
                                  </Text>
                                </Flex> */}

                                <Flex gap={4}>
                                  <Button
                                    onClick={() =>
                                      push({
                                        type: "",
                                        code: "",
                                        number: "",
                                      })
                                    }
                                    w={"120px"}
                                    h={"40px"}
                                    py={6}
                                    bg={"#D9D9D9"}
                                    borderRadius={"4px"}
                                    color={"#444444"}
                                    alignSelf={"flex-end"}
                                    alignItems={"center"}
                                    justifyContent={"center"}
                                    fontSize={"12px"}
                                    fontFamily={"PlusJakartaSansRegular"}
                                  >
                                    <Image
                                      src={
                                        require("assets/svgs/plus_dark.svg")
                                          .default
                                      }
                                      width={"16px"}
                                      height={"16px"}
                                      pos={"relative"}
                                      mr={2}
                                      _hover={{
                                        cursor: "pointer",
                                      }}
                                    />
                                    Add another
                                  </Button>
                                </Flex>
                              </>
                            )}
                          </FieldArray>
                        </Flex>
                      </Box>
                      {/* Next Button */}
                      <Flex
                        alignSelf={"flex-end"}
                        justifyContent={"flex-end"}
                        w={"100%"}
                      >
                        <Button
                          onClick={() => {
                            formik.handleSubmit();
                          }}
                          flex={1}
                          maxW={"148px"}
                          background={"#000000"}
                          color={"#FFFFFF"}
                          borderRadius={8}
                          fontSize={"12px"}
                          fontFamily={"PlusJakartaSansMedium"}
                          textTransform={"uppercase"}
                          // isDisabled={}
                          _disabled={{
                            bg: "grey.100",
                            cursor: "default",
                            _hover: {
                              bg: "grey.100",
                              cursor: "default",
                            },
                          }}
                        >
                          Next
                        </Button>
                      </Flex>
                    </Flex>
                  </Form>
                )}
              </Formik>
            </Box>
          </Flex>
        </Flex>
      </Box>
    </MainLayout>
  );
};

export default AddressDetails;
